'use client';

import { forwardRef } from 'react';
import type { PillProps as MantinePillProps } from '@mantine/core';
import { Pill as MantinePill } from '@mantine/core';

import type { LockedProps } from '../types';

export type PillProps = Omit<MantinePillProps, LockedProps>;

/**
 * ### Pill
 *
 * @description
 * Mantine's Pill minus per-call-site style overrides (see {@link LockedProps}). For a stat-style
 * label with a count badge, use `CountPill` from `@highlight-cp/core-ui-compositions` instead.
 *
 * `withRemoveButton` is only usable inside Mantine's `PillsInput`, which owns the
 * backspace-to-remove keyboard flow — Mantine renders the remove button `aria-hidden` and
 * `tabIndex={-1}`, so a standalone removable pill has no keyboard path to `onRemove`.
 *
 * The ref passes through so a pill can act as a Tooltip/Popover target.
 *
 * @example
 * ```tsx
 * <Pill>Tag</Pill>
 * ```
 *
 * @param props - {@link PillProps}
 * @returns React component
 */
const Pill = forwardRef<HTMLDivElement, PillProps>((props, ref) => (
  <MantinePill {...props} ref={ref} />
));

Pill.displayName = 'Pill';

export default Pill;
